import { Users2 } from 'lucide-react';
import { ErrorOverlay } from '@/components/ErrorOverlay';
import { Flex } from '@/components/Flex';
import { Icon } from '@/components/Icon';
import { Section } from '@/components/Section';
import { Skeleton } from '@/components/Skeleton';
import * as styles from './ResponderCount.css';

type ResponderCountProps = {
    /** `null` while still loading. */
    responders: number | null;
    /** Opt-in: omit when this data has no independent failure mode to report. */
    isError?: boolean;
    onRetry?: () => void;
};

export function ResponderCount({ responders, isError, onRetry }: ResponderCountProps) {
    if (isError) return <ErrorOverlay active onRetry={onRetry} message="回答人数の取得に失敗しました。" />;

    // Compared against `null` rather than checked for falsiness: zero responders is
    // a legitimate count, not a missing one.
    if (responders === null) return <ResponderCountSkeleton />;

    return <ResponderCountContent responders={responders} />;
}

type ResponderCountContentProps = {
    [K in keyof Pick<ResponderCountProps, 'responders'>]: NonNullable<ResponderCountProps[K]>;
};

function ResponderCountContent({ responders }: ResponderCountContentProps) {
    return (
        <Section pt="8" pb="8" flex>
            <Flex gap="8" align="center">
                <div className={styles.iconCircle}>
                    <Icon icon={Users2} size="lg" className={styles.icon} />
                </div>

                <Flex direction="column" gap="2">
                    <div className={styles.title}>回答人数</div>

                    <div className={styles.value}>{responders}人</div>

                    <div className={styles.subtext}>がサーベイに回答しました</div>
                </Flex>
            </Flex>
        </Section>
    );
}

export function ResponderCountSkeleton() {
    return (
        <Section pt="8" pb="8" flex>
            <Flex gap="8" align="center">
                <Skeleton.Circle radius={56} />

                <Flex direction="column" gap="4" flex>
                    <Skeleton.Line width="30%" height="sm" />
                    <Skeleton.Line width="40%" height="4xl" />
                    <Skeleton.Line width="70%" height="xs" />
                </Flex>
            </Flex>
        </Section>
    );
}
